// BillingPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react'; // Import useRef
import { PlusCircle, Trash2, FileText, User, Calendar, MapPin, DollarSign, Percent, Weight } from 'lucide-react';
import html2pdf from 'html2pdf.js'; // Import html2pdf.js

interface InvoiceProduct {
  id: string;
  itemName: string;
  materialType: string;
  purity: string;
  weight: number; // in grams
  quantity: number;
  unitPrice: number; // Calculated live metal price per gram/kg
  makingCharges: number;
  discount: number; // Percentage
  tax: number;     // Percentage
  calculatedItemTotal: number;
}

const BillingPage: React.FC = () => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]); // Current date

  // Product Line Item Input States
  const [newItemName, setNewItemName] = useState('');
  const [newItemMaterialType, setNewItemMaterialType] = useState('');
  const [newItemPurity, setNewItemPurity] = useState('');
  const [newItemWeight, setNewItemWeight] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('1');
  const [newItemMakingCharges, setNewItemMakingCharges] = useState('');
  const [newItemDiscount, setNewItemDiscount] = useState('');
  const [newItemTax, setNewItemTax] = useState('');

  // List of products in the invoice
  const [products, setProducts] = useState<InvoiceProduct[]>([]);

  // Live price states (simulated)
  const [selectedCity, setSelectedCity] = useState<string>('Delhi'); // For fetching simulated live prices
  const [liveGold24KPrice, setLiveGold24KPrice] = useState<number | null>(null); // Per gram
  const [liveSilverPriceKg, setLiveSilverPriceKg] = useState<number | null>(null); // Per kg

  // Messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const indianCities = [
    'Delhi', 'Mumbai', 'Chennai', 'Kolkata', 'Hyderabad', 'Bengaluru',
    'Ahmedabad', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore'
  ];

  const goldPurities = ['18K', '22K', '24K'];
  const silverPurities = ['925', '999'];

  // Ref for the content to be converted to PDF
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Helper to format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null || isNaN(amount)) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // --- Simulated Live Price Fetching ---
  const getSimulatedPrices = useCallback((city: string) => {
    let baseGold24K_per_gram = 11080; // Adjusted for per gram
    let baseSilverKg = 90000;

    switch (city) {
      case 'Delhi':
        baseGold24K_per_gram = 10500 + Math.random() * 50; // Delhi slightly higher, reflecting current rates
        baseSilverKg = 90000 - Math.random() * 200;
        break;
      case 'Mumbai':
        baseGold24K_per_gram = 10000 + Math.random() * 60;
        baseSilverKg = 90200 + Math.random() * 150;
        break;
      case 'Chennai':
        baseGold24K_per_gram = 11000 + Math.random() * 40;
        baseSilverKg = 89800 - Math.random() * 100;
        break;
      case 'Kolkata':
        baseGold24K_per_gram = 10980 + Math.random() * 55;
        baseSilverKg = 90100 + Math.random() * 180;
        break;
      case 'Hyderabad':
        baseGold24K_per_gram = 10550 + Math.random() * 45;
        baseSilverKg = 89900 - Math.random() * 250;
        break;
      default: // For other cities, a general range
        baseGold24K_per_gram = 10150 + Math.random() * 70;
        baseSilverKg = 89700 + Math.random() * 300;
        break;
    }


    const goldFluctuation = (Math.random() * 0.0002 - 0.0001) * baseGold24K_per_gram;
    const silverFluctuation = (Math.random() * 0.0005 - 0.00025) * baseSilverKg;

    const newGold24K = parseFloat((baseGold24K_per_gram + goldFluctuation).toFixed(2));
    const newSilverKg = parseFloat((baseSilverKg + silverFluctuation).toFixed(2));

    return {
      gold24K: newGold24K,
      gold22K: parseFloat((newGold24K * (22 / 24)).toFixed(2)),
      gold18K: parseFloat((newGold24K * (18 / 24)).toFixed(2)),
      silverKg: newSilverKg,
    };
  }, []);

  // Effect to fetch (simulated) live prices based on selected city
  useEffect(() => {
    const simulatedData = getSimulatedPrices(selectedCity);
    setLiveGold24KPrice(simulatedData.gold24K);
    setLiveSilverPriceKg(simulatedData.silverKg);
  }, [selectedCity, getSimulatedPrices]);

  // Calculate individual item total
  const calculateItemTotal = useCallback((product: Partial<InvoiceProduct>) => {
    let baseMetalCost = 0;
    const parsedWeight = product.weight || 0;
    const parsedQuantity = product.quantity || 1;
    const parsedUnitPrice = product.unitPrice || 0;
    const parsedMakingCharges = product.makingCharges || 0;
    const parsedDiscount = product.discount || 0;
    const parsedTax = product.tax || 0;

    // Base metal cost based on unitPrice (which comes from live rates)
    baseMetalCost = parsedUnitPrice * parsedWeight * parsedQuantity;

    let priceBeforeDiscount = baseMetalCost + (parsedMakingCharges * parsedQuantity); // Making charges apply per quantity
    let priceAfterDiscount = priceBeforeDiscount * (1 - (parsedDiscount / 100));
    let finalPrice = priceAfterDiscount * (1 + (parsedTax / 100));

    return parseFloat(finalPrice.toFixed(2));
  }, []);

  // Add new product to invoice
  const handleAddItem = () => {
    setErrorMessage(null);
    if (!newItemName || !newItemMaterialType || !newItemPurity || !newItemWeight || !newItemQuantity) {
      setErrorMessage('Please fill in all required fields for the new item.');
      return;
    }
    if (liveGold24KPrice === null || liveSilverPriceKg === null) {
      setErrorMessage('Live prices not loaded yet. Please wait or select a city.');
      return;
    }

    let unitPrice = 0; // Price per gram of the metal
    const parsedWeight = parseFloat(newItemWeight);
    const parsedQuantity = parseInt(newItemQuantity);
    const parsedMakingCharges = parseFloat(newItemMakingCharges) || 0;
    const parsedDiscount = parseFloat(newItemDiscount) || 0;
    const parsedTax = parseFloat(newItemTax) || 0;

    if (newItemMaterialType === 'Gold') {
      let purityFactor = 0;
      if (newItemPurity === '24K') purityFactor = 24 / 24;
      else if (newItemPurity === '22K') purityFactor = 22 / 24;
      else if (newItemPurity === '18K') purityFactor = 18 / 24;
      unitPrice = (liveGold24KPrice || 0) * purityFactor; // Price per gram of specific purity gold
    } else if (newItemMaterialType === 'Silver') {
      unitPrice = (liveSilverPriceKg || 0) / 1000; // Price per gram of silver
    } else {
      setErrorMessage('Invalid material type selected for new item.');
      return;
    }

    const newProduct: InvoiceProduct = {
      id: Date.now().toString(), // Simple unique ID
      itemName: newItemName,
      materialType: newItemMaterialType,
      purity: newItemPurity,
      weight: parsedWeight,
      quantity: parsedQuantity,
      unitPrice: parseFloat(unitPrice.toFixed(2)), // Store unit price for calculation
      makingCharges: parsedMakingCharges,
      discount: parsedDiscount,
      tax: parsedTax,
      calculatedItemTotal: 0 // Will be calculated after all other fields are set
    };
    newProduct.calculatedItemTotal = calculateItemTotal(newProduct);

    setProducts((prev) => [...prev, newProduct]);

    // Clear new item fields
    setNewItemName('');
    setNewItemMaterialType('');
    setNewItemPurity('');
    setNewItemWeight('');
    setNewItemQuantity('1');
    setNewItemMakingCharges('');
    setNewItemDiscount('');
    setNewItemTax('');
  };

  const handleRemoveItem = (id: string) => {
    setProducts((prev) => prev.filter((product) => product.id !== id));
  };

  // Calculate totals for the entire invoice
  const subtotal = products.reduce((sum, item) => sum + item.calculatedItemTotal, 0);
  const totalDiscount = products.reduce((sum, item) => {
    const baseMetalCost = item.unitPrice * item.weight * item.quantity;
    const priceBeforeDiscount = baseMetalCost + (item.makingCharges * item.quantity);
    return sum + (priceBeforeDiscount * (item.discount / 100));
  }, 0);
  const totalTax = products.reduce((sum, item) => {
    const baseMetalCost = item.unitPrice * item.weight * item.quantity;
    const priceBeforeDiscount = baseMetalCost + (item.makingCharges * item.quantity);
    const priceAfterDiscount = priceBeforeDiscount * (1 - (item.discount / 100));
    return sum + (priceAfterDiscount * (item.tax / 100));
  }, 0);
  const grandTotal = subtotal; // Subtotal already includes discount and tax from item calculation


  const handleGenerateInvoice = () => {
    setErrorMessage(null);
    if (!customerName || !customerPhone || !invoiceNumber || !invoiceDate) {
      setErrorMessage('Please fill in all customer and invoice details.');
      return;
    }
    if (products.length === 0) {
      setErrorMessage('Please add at least one product to the invoice.');
      return;
    }

    // In a real application, you would send this data to your backend
    const invoiceData = {
      invoiceNumber,
      invoiceDate,
      customerDetails: {
        name: customerName,
        phone: customerPhone,
        email: customerEmail,
        address: customerAddress,
      },
      products,
      summary: {
        subtotal: parseFloat(subtotal.toFixed(2)),
        totalDiscount: parseFloat(totalDiscount.toFixed(2)),
        totalTax: parseFloat(totalTax.toFixed(2)),
        grandTotal: parseFloat(grandTotal.toFixed(2)),
      },
      liveRatesUsed: {
        city: selectedCity,
        gold24K_per_gram: liveGold24KPrice,
        silverKg: liveSilverPriceKg,
      }
    };

    console.log("Invoice Generated:", invoiceData);
    setSuccessMessage('Invoice generated successfully! Check console for data.');

    // --- PDF Generation Logic ---
    if (invoiceRef.current) {
      html2pdf(invoiceRef.current, {
        margin: 1,
        filename: `invoice_${invoiceNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      });
    }
    // --- End PDF Generation Logic ---


    // Reset form after successful generation (optional)
    // setCustomerName('');
    // setCustomerPhone('');
    // setCustomerEmail('');
    // setCustomerAddress('');
    // setInvoiceNumber('');
    // setProducts([]);
    // setNewItemName('');
    // setNewItemMaterialType('');
    // setNewItemPurity('');
    // setNewItemWeight('');
    // setNewItemQuantity('1');
    // setNewItemMakingCharges('');
    // setNewItemDiscount('');
    // setNewItemTax('');
  };


  return (
    <div className="min-h-screen bg-gray-100 p-8">
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="fixed top-4 right-4 bg-red-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">
          {errorMessage}
        </div>
      )}

      {/* Input Section - This entire div will be hidden when printing */}
      <div className="print:hidden bg-white rounded-xl shadow-2xl p-8 max-w-6xl mx-auto my-8">
        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-8 flex items-center justify-center">
          <FileText className="w-10 h-10 text-blue-600 mr-3" />
          Generate Invoice
        </h1>

        {/* Live Rates & City Selection - You can keep this here or move into the print section if you want it on PDF */}
        <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <MapPin className="w-7 h-7 text-blue-500 mr-2" /> Current Live Rates (Simulated)
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-4">
            <label htmlFor="live-city-select" className="text-lg font-medium text-gray-700 flex items-center">
              Select City:
            </label>
            <select
              id="live-city-select"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-800"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              {indianCities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Gold 24K (per gram)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveGold24KPrice)}</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Gold 22K (per gram)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveGold24KPrice !== null ? liveGold24KPrice * (22 / 24) : null)}</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Silver (per kg)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveSilverPriceKg)}</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-4 text-sm italic">
            These rates are used for calculating item base prices.
          </p>
        </div>


        {/* Customer Details Input */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <User className="w-6 h-6 text-gray-600 mr-2" /> Customer Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">Customer Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                id="customerName"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
              <input
                type="tel"
                id="customerPhone"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="customerEmail"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="customerAddress" className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                id="customerAddress"
                rows={2}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>

        {/* Invoice Details Input */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FileText className="w-6 h-6 text-gray-600 mr-2" /> Invoice Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-1">Invoice Number <span className="text-red-500">*</span></label>
              <input
                type="text"
                id="invoiceNumber"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-1">Invoice Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                id="invoiceDate"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Add Product Section */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <PlusCircle className="w-6 h-6 text-green-600 mr-2" /> Add Product
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="newItemName" className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                id="newItemName"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g., Gold Ring"
                required
              />
            </div>
            <div>
              <label htmlFor="newItemMaterialType" className="block text-sm font-medium text-gray-700 mb-1">Material Type <span className="text-red-500">*</span></label>
              <select
                id="newItemMaterialType"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newItemMaterialType}
                onChange={(e) => {
                  setNewItemMaterialType(e.target.value);
                  setNewItemPurity(''); // Reset purity when material type changes
                }}
                required
              >
                <option value="">Select Material</option>
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
              </select>
            </div>
            <div>
              <label htmlFor="newItemPurity" className="block text-sm font-medium text-gray-700 mb-1">Purity <span className="text-red-500">*</span></label>
              <select
                id="newItemPurity"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newItemPurity}
                onChange={(e) => setNewItemPurity(e.target.value)}
                required
                disabled={!newItemMaterialType}
              >
                <option value="">Select Purity</option>
                {newItemMaterialType === 'Gold' && goldPurities.map((p) => <option key={p} value={p}>{p}</option>)}
                {newItemMaterialType === 'Silver' && silverPurities.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="newItemWeight" className="block text-sm font-medium text-gray-700 mb-1">Weight (grams) <span className="text-red-500">*</span></label>
              <input
                type="number"
                id="newItemWeight"
                step="0.01"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newItemWeight}
                onChange={(e) => setNewItemWeight(e.target.value)}
                placeholder="e.g., 5.25"
                required
              />
            </div>
            <div>
              <label htmlFor="newItemQuantity" className="block text-sm font-medium text-gray-700 mb-1">Quantity <span className="text-red-500">*</span></label>
              <input
                type="number"
                id="newItemQuantity"
                step="1"
                min="1"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={newItemQuantity}
                onChange={(e) => setNewItemQuantity(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="newItemMakingCharges" className="block text-sm font-medium text-gray-700 mb-1">Making Charges (per item)</label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  {/* Rupee Symbol SVG */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M10.164 13.914L10 13.75a1 1 0 010-2h4a1 1 0 010 2h-4.164zM16 11.75a1 1 0 010-2h2.5c.276 0 .5.224.5.5s-.224.5-.5.5H16zm0-4a1 1 0 010-2h3c.276 0 .5.224.5.5s-.224.5-.5.5H16zM13 18.75a1 1 0 010-2h-3.5a.5.5 0 00-.5.5v2a.5.5 0 00.5.5H13z" />
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 5a1 1 0 00-1 1v2.25c0 .276.224.5.5.5h6.5a.5.5 0 01.5.5v1.5a.5.5 0 01-.5.5h-5.5a1 1 0 00-1 1v2.25a1 1 0 001 1h4a1 1 0 001-1V12.5a.5.5 0 00-.5-.5h-6.5a.5.5 0 01-.5-.5V10c0-.276-.224-.5-.5-.5H8a1 1 0 00-1 1v2.25c0 .276.224.5.5.5h3.5a.5.5 0 01.5.5v1.5a.5.5 0 01-.5.5H8a1 1 0 00-1 1v.75a1 1 0 001 1h4a2 2 0 002-2V8a2 2 0 00-2-2H8z" clipRule="evenodd" />
                  </svg>
                </span>
                <input
                  type="number"
                  id="newItemMakingCharges"
                  step="0.01"
                  className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={newItemMakingCharges}
                  onChange={(e) => setNewItemMakingCharges(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label htmlFor="newItemDiscount" className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  <Percent className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  id="newItemDiscount"
                  step="0.01"
                  className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={newItemDiscount}
                  onChange={(e) => setNewItemDiscount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label htmlFor="newItemTax" className="block text-sm font-medium text-gray-700 mb-1">Tax (%)</label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'%3E%3C/path%3E%3Cpolyline points='14 2 14 8 20 8'%3E%3C/polyline%3E%3Cline x1='12' y1='17' x2='12' y2='11'%3E%3C/line%3E%3Cline x1='9' y1='14' x2='15' y2='14'%3E%3C/line%3E%3C/svg%3E" alt="Tax Icon" className="h-5 w-5" />
                </span>
                <input
                  type="number"
                  id="newItemTax"
                  step="0.01"
                  className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={newItemTax}
                  onChange={(e) => setNewItemTax(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition duration-200 flex items-center justify-center"
          >
            <PlusCircle className="w-5 h-5 mr-2" /> Add Item to Invoice
          </button>
          {products.length === 0 && (
            <p className="text-gray-500 text-center mt-4">No items added yet. Use the section above to add products.</p>
          )}
        </div>

        {/* Generate Invoice Button for the interactive page */}
        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={handleGenerateInvoice}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-xl transition duration-200 shadow-lg"
          >
            Generate Invoice PDF
          </button>
        </div>
      </div> {/* End of the interactive input section */}

      {/* Invoice Display Section - This is what gets printed/PDF-ed */}
      <div ref={invoiceRef} className="bg-white rounded-xl shadow-2xl p-8 max-w-6xl mx-auto my-8 print:shadow-none print:border-none">
        {/* Company Name at the top */}
        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-4">Sardar Jewellers</h1>
        <p className="text-lg text-center text-gray-700 mb-8">Your Trusted Jewellery Partner</p>

        {/* Invoice Title for PDF */}
        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-8 flex items-center justify-center">
          <FileText className="w-10 h-10 text-blue-600 mr-3" />
          Invoice
        </h1>

        {/* Live Rates & City Selection for display in PDF (optional, based on your preference) */}
        <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <MapPin className="w-7 h-7 text-blue-500 mr-2" /> Current Live Rates (Simulated)
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-4">
            <p className="text-lg font-medium text-gray-700 flex items-center">
              Selected City: {selectedCity}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Gold 24K (per gram)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveGold24KPrice)}</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Gold 22K (per gram)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveGold24KPrice !== null ? liveGold24KPrice * (22 / 24) : null)}</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-md shadow-sm">
              <p className="text-lg font-semibold text-blue-800">Silver (per kg)</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(liveSilverPriceKg)}</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-4 text-sm italic">
            These rates are used for calculating item base prices.
          </p>
        </div>


        {/* Customer Details - Display only */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <User className="w-6 h-6 text-gray-600 mr-2" /> Customer Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <p><strong>Name:</strong> {customerName}</p>
            <p><strong>Phone:</strong> {customerPhone}</p>
            <p><strong>Email:</strong> {customerEmail}</p>
            <p className="md:col-span-2"><strong>Address:</strong> {customerAddress}</p>
          </div>
        </div>

        {/* Invoice Details - Display only */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FileText className="w-6 h-6 text-gray-600 mr-2" /> Invoice Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <p><strong>Invoice Number:</strong> {invoiceNumber}</p>
            <p><strong>Invoice Date:</strong> {invoiceDate}</p>
          </div>
        </div>

        {/* Invoice Products Table */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Invoice Items</h2>
          {products.length === 0 ? (
            <p className="text-gray-500 text-center">No items added to this invoice.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material/Purity</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight (g)</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">M. Charges (₹)</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disc (%)</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax (%)</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    {/* No Actions column in the PDF view */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.itemName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{`${product.materialType} (${product.purity})`}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.weight}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(product.makingCharges * product.quantity)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.discount}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.tax}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{formatCurrency(product.calculatedItemTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invoice Summary */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Invoice Summary</h2>
          <div className="flex flex-col items-end space-y-2">
            <div className="flex justify-between w-full max-w-sm text-lg text-gray-700">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between w-full max-w-sm text-lg text-gray-700">
              <span>Total Discount:</span>
              <span>- {formatCurrency(totalDiscount)}</span>
            </div>
            <div className="flex justify-between w-full max-w-sm text-lg text-gray-700">
              <span>Total Tax:</span>
              <span>+ {formatCurrency(totalTax)}</span>
            </div>
            <div className="flex justify-between w-full max-w-sm text-2xl font-extrabold text-blue-700 border-t-2 border-blue-300 pt-2 mt-2">
              <span>Grand Total:</span>
              <span>{formatCurrency(grandTotal)}</span>
            </div>
          </div>
        </div>
      </div> {/* End of invoiceRef div */}
    </div>
  );
};

export default BillingPage;